import { Injectable } from '@angular/core';

interface WaveformCacheEntry {
  audioBuffer: AudioBuffer;
  waveformPath: string;
  duration: number;
  lastModified: number;
  width: number;
}

interface WaveformCacheStorage {
  [trackId: string]: {
    waveformPath: string;
    duration: number;
    lastModified: number;
    width: number;
    audioUrl: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class AudioCacheService {
  private readonly CACHE_KEY = 'mzic-audio-waveform-cache';
  private readonly CACHE_EXPIRY_MS = 1 * 60 * 60 * 1000; // 1 hour - perfect for editing sessions
  private readonly MAX_CACHE_SIZE = 50; // Maximum number of cached items

  // Memory cache for active session (includes AudioBuffer)
  private memoryCache = new Map<string, WaveformCacheEntry>();

  constructor() {
    this.cleanExpiredCache();
    this.restoreFromStorage();

    const stats = this.getCacheStats();
    console.log(
      `🎵 AudioCacheService initialized with 1-hour expiry for editing sessions`,
    );
    console.log(
      `🎵 Restored cache: ${stats.storageEntries} entries (${stats.totalSizeKB}KB)`,
    );
  }

  /**
   * Restore waveform paths from localStorage on service initialization
   * Note: AudioBuffers cannot be serialized, so they'll be regenerated as needed
   */
  private restoreFromStorage(): void {
    try {
      const cacheData = localStorage.getItem(this.CACHE_KEY);
      if (!cacheData) return;

      const cache: WaveformCacheStorage = JSON.parse(cacheData);
      let restoredCount = 0;

      Object.entries(cache).forEach(([trackId, entry]) => {
        const now = Date.now();
        const isNotExpired = now - entry.lastModified < this.CACHE_EXPIRY_MS;

        if (isNotExpired) {
          // Create memory cache entry without AudioBuffer (will be generated when needed)
          this.memoryCache.set(trackId, {
            audioBuffer: null as any, // Will be generated when needed
            waveformPath: entry.waveformPath,
            duration: entry.duration,
            lastModified: entry.lastModified,
            width: entry.width,
          });
          restoredCount++;
        }
      });

      if (restoredCount > 0) {
        console.log(
          `🎵 Restored ${restoredCount} waveform cache entries from storage`,
        );
      }
    } catch (error) {
      console.warn('Failed to restore cache from storage:', error);
    }
  }

  /**
   * Get cached waveform data for a track
   */
  getCachedWaveform(
    trackId: string,
    audioUrl: string,
    requiredWidth: number,
  ): WaveformCacheEntry | null {
    console.log(
      `🎵 Looking up cache for track ${trackId}, width: ${requiredWidth}`,
    );
    console.log(
      `🎵 Memory cache size: ${this.memoryCache.size}, has trackId: ${this.memoryCache.has(trackId)}`,
    );

    // Check memory cache first (includes AudioBuffer)
    const memoryCached = this.memoryCache.get(trackId);
    if (memoryCached) {
      const isValid = this.isValidCacheEntry(memoryCached, requiredWidth);
      console.log(`🎵 Memory cache entry found:`, {
        hasAudioBuffer: !!memoryCached.audioBuffer,
        audioBufferLength: memoryCached.audioBuffer?.length || 0,
        hasWaveformPath: !!memoryCached.waveformPath,
        isValid,
        width: memoryCached.width,
        age:
          Math.round((Date.now() - memoryCached.lastModified) / 1000 / 60) +
          ' minutes',
      });

      if (isValid) {
        console.log(
          `🎵 Cache HIT (memory) for track ${trackId} - instant load!`,
        );
        return memoryCached;
      }
    }

    // Check localStorage cache (waveform path only, no AudioBuffer)
    const storageCached = this.getFromStorage(trackId);
    if (storageCached) {
      const isValid = this.isValidStorageCacheEntry(
        storageCached,
        requiredWidth,
        audioUrl,
      );
      console.log(`🎵 Storage cache found for track ${trackId}:`, {
        cached: !!storageCached,
        isValid,
        cachedUrl: this.extractBaseUrl(storageCached.audioUrl),
        currentUrl: this.extractBaseUrl(audioUrl),
        age:
          Math.round((Date.now() - storageCached.lastModified) / 1000 / 60) +
          ' minutes',
      });

      if (isValid) {
        console.log(
          `🎵 Cache HIT (storage) for track ${trackId} - fast load after page reload!`,
        );
        // Return partial data - AudioBuffer will need to be regenerated
        return {
          audioBuffer: null as any, // Will be regenerated
          waveformPath: storageCached.waveformPath,
          duration: storageCached.duration,
          lastModified: storageCached.lastModified,
          width: storageCached.width,
        };
      }
    }

    console.log(
      `🎵 Cache MISS for track ${trackId} - generating fresh waveform (expires in 1 hour)`,
    );
    return null;
  }

  /**
   * Cache waveform data for a track
   */
  cacheWaveform(
    trackId: string,
    audioUrl: string,
    entry: WaveformCacheEntry,
  ): void {
    // Store in memory cache (includes AudioBuffer)
    this.memoryCache.set(trackId, entry);

    // Store in localStorage (waveform path only, no AudioBuffer)
    this.saveToStorage(trackId, audioUrl, entry);

    // Cleanup if cache gets too large
    this.cleanupCache();

    const stats = this.getCacheStats();
    console.log(
      `🎵 Cached waveform for track ${trackId} (width: ${entry.width}px, expires in 1 hour)`,
    );
    console.log(
      `🎵 Cache stats: ${stats.memoryEntries} memory + ${stats.storageEntries} storage = ${stats.totalSizeKB}KB`,
    );
  }

  /**
   * Check if cached entry is valid for the required width
   */
  private isValidCacheEntry(
    entry: WaveformCacheEntry,
    requiredWidth: number,
  ): boolean {
    const now = Date.now();
    const isNotExpired = now - entry.lastModified < this.CACHE_EXPIRY_MS;
    const widthDifference = Math.abs(entry.width - requiredWidth);
    const isWidthValid = widthDifference <= 10; // Allow 10px difference

    return isNotExpired && isWidthValid;
  }

  /**
   * Check if storage cached entry is valid
   */
  private isValidStorageCacheEntry(
    entry: {
      waveformPath: string;
      duration: number;
      lastModified: number;
      width: number;
      audioUrl: string;
    },
    requiredWidth: number,
    audioUrl: string,
  ): boolean {
    const now = Date.now();
    const isNotExpired = now - entry.lastModified < this.CACHE_EXPIRY_MS;
    const widthDifference = Math.abs(entry.width - requiredWidth);
    const isWidthValid = widthDifference <= 10;

    // For signed S3 URLs, don't validate exact URL match since signatures change
    // The trackId is already a unique identifier for the track
    // Only check if the base file path is similar (remove query parameters)
    const isSimilarFile =
      this.extractBaseUrl(entry.audioUrl) === this.extractBaseUrl(audioUrl);

    return isNotExpired && isWidthValid && isSimilarFile;
  }

  /**
   * Extract base URL without query parameters for S3 signed URLs
   */
  private extractBaseUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      return urlObj.origin + urlObj.pathname; // Remove query parameters (signatures, etc)
    } catch {
      return url.split('?')[0]; // Fallback: remove everything after ?
    }
  }

  /**
   * Get cache entry from localStorage
   */
  private getFromStorage(trackId: string): {
    waveformPath: string;
    duration: number;
    lastModified: number;
    width: number;
    audioUrl: string;
  } | null {
    try {
      const cacheData = localStorage.getItem(this.CACHE_KEY);
      if (!cacheData) return null;

      const cache: WaveformCacheStorage = JSON.parse(cacheData);
      const entry = cache[trackId];

      return entry || null;
    } catch (error) {
      console.warn('Failed to read waveform cache from storage:', error);
      return null;
    }
  }

  /**
   * Save cache entry to localStorage
   */
  private saveToStorage(
    trackId: string,
    audioUrl: string,
    entry: WaveformCacheEntry,
  ): void {
    try {
      const cacheData = localStorage.getItem(this.CACHE_KEY);
      const cache: WaveformCacheStorage = cacheData
        ? JSON.parse(cacheData)
        : {};

      // Store only serializable data (no AudioBuffer)
      cache[trackId] = {
        waveformPath: entry.waveformPath,
        duration: entry.duration,
        lastModified: entry.lastModified,
        width: entry.width,
        audioUrl: audioUrl,
      };

      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      console.warn('Failed to save waveform cache to storage:', error);
    }
  }

  /**
   * Clean up expired cache entries
   */
  private cleanExpiredCache(): void {
    try {
      const cacheData = localStorage.getItem(this.CACHE_KEY);
      if (!cacheData) return;

      const cache: WaveformCacheStorage = JSON.parse(cacheData);
      const now = Date.now();
      const cleanedCache: WaveformCacheStorage = {};

      Object.entries(cache).forEach(([trackId, entry]) => {
        if (now - entry.lastModified < this.CACHE_EXPIRY_MS) {
          cleanedCache[trackId] = entry;
        }
      });

      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cleanedCache));
    } catch (error) {
      console.warn('Failed to clean expired cache:', error);
    }
  }

  /**
   * Clean up cache if it gets too large
   */
  private cleanupCache(): void {
    try {
      // Clean memory cache
      if (this.memoryCache.size > this.MAX_CACHE_SIZE) {
        // Remove oldest entries
        const entries = Array.from(this.memoryCache.entries());
        entries.sort((a, b) => a[1].lastModified - b[1].lastModified);

        const toRemove = entries.slice(0, entries.length - this.MAX_CACHE_SIZE);
        toRemove.forEach(([key]) => this.memoryCache.delete(key));
      }

      // Clean localStorage cache
      const cacheData = localStorage.getItem(this.CACHE_KEY);
      if (!cacheData) return;

      const cache: WaveformCacheStorage = JSON.parse(cacheData);
      const entries = Object.entries(cache);

      if (entries.length > this.MAX_CACHE_SIZE) {
        entries.sort((a, b) => a[1].lastModified - b[1].lastModified);
        const toKeep = entries.slice(-this.MAX_CACHE_SIZE);

        const cleanedCache: WaveformCacheStorage = {};
        toKeep.forEach(([key, value]) => {
          cleanedCache[key] = value;
        });

        localStorage.setItem(this.CACHE_KEY, JSON.stringify(cleanedCache));
      }
    } catch (error) {
      console.warn('Failed to cleanup cache:', error);
    }
  }

  /**
   * Clear all cache data
   */
  clearCache(): void {
    this.memoryCache.clear();
    localStorage.removeItem(this.CACHE_KEY);
  }

  /**
   * Remove specific track from cache
   */
  removeFromCache(trackId: string): void {
    this.memoryCache.delete(trackId);

    try {
      const cacheData = localStorage.getItem(this.CACHE_KEY);
      if (cacheData) {
        const cache: WaveformCacheStorage = JSON.parse(cacheData);
        delete cache[trackId];
        localStorage.setItem(this.CACHE_KEY, JSON.stringify(cache));
      }
    } catch (error) {
      console.warn('Failed to remove track from cache:', error);
    }
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): {
    memoryEntries: number;
    storageEntries: number;
    totalSizeKB?: number;
  } {
    const memoryEntries = this.memoryCache.size;

    try {
      const cacheData = localStorage.getItem(this.CACHE_KEY);
      const storageEntries = cacheData
        ? Object.keys(JSON.parse(cacheData)).length
        : 0;
      const totalSizeKB = cacheData
        ? Math.round(new Blob([cacheData]).size / 1024)
        : 0;

      return { memoryEntries, storageEntries, totalSizeKB };
    } catch (error) {
      return { memoryEntries, storageEntries: 0 };
    }
  }
}
